import { Fragment } from "react/jsx-runtime";
import { Chapter, TemplateType } from "../types";
import {
  getChapterHasTransition,
  getClosingEnabled,
  getFirstSentence,
  getIntroEnabled,
} from "../helpers";
import { useAntTheme } from "hooks/useAntTheme";
import { CheckboxChangeEvent, Flex, Typography } from "antd";
import { PerChapterTransitionSwitch } from "./common/PerChapterTransitionSwitch";
import { IntroClosingEnableSwitch } from "./common/IntroClosingEnableSwitch";
import { useTemplateEditorStore } from "../store/useTemplateEditorStore";
import { useTemplateData } from "../hooks/useTemplateData";
import {
  LAYOUT_PREVIEW_OUTLINE_WIDTH,
  STORYBOARD_THUMBNAILS_PADDING,
  STORYBOARD_THUMBNAILS_WIDTH,
} from "../constants";
import { TemplatePreview } from "./Template/TemplatePreview";
import { useEffect, useRef } from "react";
import { SectionWithSlides } from "hooks/useStoryboardWithSlides/";
import {
  ChapterCheckbox,
  LayoutWrapper,
  StyledScrollableFlex,
} from "./StoryboardSlidesThumbnails";
import { parseScript } from "pages/media/helpers/scriptAnchors";
import { useTranslate } from "@refinedev/core";
import { radius } from "@libertify/ui";

const THUMBNAIL_WRAPPER_WIDTH = "96%";
export const StoryboardThumbnails = ({
  handleCardClick,
  sections,
  hasTransition,
  showCheckbox = false,
  checkedChapters = [],
  handleChapterCheckboxChange,
  visibilitySettings = {},
}: {
  handleCardClick?: (chapter: Chapter) => void;
  sections: SectionWithSlides[];
  hasTransition: boolean;
  showCheckbox?: boolean;
  checkedChapters?: string[];
  handleChapterCheckboxChange?: (
    e: CheckboxChangeEvent,
    templateId?: string
  ) => void;
  visibilitySettings?: {
    hideIntro?: boolean;
    hideClosing?: boolean;
    hideTransitions?: boolean;
    hideChapters?: boolean;
  };
}) => {
  const { theme } = useAntTheme();
  const t = useTranslate();

  const {
    brandKitData: { intro, closing },
    mediaGenerationSettingsData: {
      mediaAspectRatio,
      mediaGenerationSettings,
      setChapterTransitionVisibility,
    },
  } = useTemplateData();

  const {
    introduction_template_id: introductionTemplate,
    closing_template_id: closingTemplate,
  } = mediaGenerationSettings ?? {};

  const { currentChapter } = useTemplateEditorStore((state) => ({
    currentChapter: state.currentChapter,
  }));

  const selectetThumbnailRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectetThumbnailRef.current) {
      selectetThumbnailRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentChapter]);

  const generateCommonTemplateBoardPreviewProps = (chapter: Chapter) => {
    const isSelectedThumbnail =
      currentChapter.topic === chapter.topic &&
      currentChapter.type === chapter.type;
    return {
      ref: isSelectedThumbnail ? selectetThumbnailRef : undefined,
      chapter,
      border: {
        borderColor: isSelectedThumbnail ? theme.colorPrimary : undefined,
      },
      templateSize: {
        width:
          STORYBOARD_THUMBNAILS_WIDTH -
          STORYBOARD_THUMBNAILS_PADDING * 2 -
          LAYOUT_PREVIEW_OUTLINE_WIDTH * 2,
        height: Math.round(
          (STORYBOARD_THUMBNAILS_WIDTH -
            STORYBOARD_THUMBNAILS_PADDING * 2 -
            LAYOUT_PREVIEW_OUTLINE_WIDTH * 2) /
            (mediaAspectRatio ?? 9 / 16)
        ),
      },
      opacity: 1,
    };
  };

  const renderChapterWrapper = (
    previewText: string,
    chapter: Chapter,
    index: number
  ) => {
    const onChapterCardClick = () => {
      handleCardClick?.({
        templateId: chapter.templateId,
        topic: chapter.topic,
        sectionId: chapter.sectionId,
        type: TemplateType.CHAPTER,
      });
    };

    const defaultPreviewProps = {
      ...generateCommonTemplateBoardPreviewProps(chapter),
      previewText,
      onCardClick: onChapterCardClick,
    };

    const isCheckedChapter = checkedChapters.includes(chapter.templateId ?? "");
    const isCurrentChapter = currentChapter.topic === chapter.topic;

    return (
      <Flex align="start" gap={10}>
        <LayoutWrapper>
          <Flex
            justify={showCheckbox ? "space-between" : "center"}
            style={{ width: THUMBNAIL_WRAPPER_WIDTH }}
          >
            <Typography.Text
              style={{
                fontSize: "12px",
                textTransform: "uppercase",
              }}
            >
              {t(
                "components.templates.TemplatesBoard.storyboardSlidesThumbnails.scene",
                {
                  number: index + 1,
                }
              )}
            </Typography.Text>
            {showCheckbox && (
              <ChapterCheckbox
                disabled={isCurrentChapter}
                checked={isCurrentChapter || isCheckedChapter}
                onChange={(e) =>
                  handleChapterCheckboxChange?.(e, chapter.templateId)
                }
              />
            )}
          </Flex>
          <TemplatePreview {...defaultPreviewProps} />
        </LayoutWrapper>
      </Flex>
    );
  };

  const introductionChapter = {
    topic: "Introduction",
    sectionId: "",
    type: TemplateType.INTRODUCTION,
  };

  const closingChapter = {
    topic: "Closing",
    sectionId: "",
    type: TemplateType.CLOSING,
  };

  const showIntro = !visibilitySettings.hideIntro && intro;
  const showClosing = !visibilitySettings.hideClosing && closing;
  const introEnabled = getIntroEnabled(mediaGenerationSettings, !!intro);
  const closingEnabled = getClosingEnabled(mediaGenerationSettings, !!closing);
  // The transition header (with the per-chapter switch) is always rendered
  // so users can flip a chapter's transition back on after disabling it.
  const showTransitions = !visibilitySettings.hideTransitions;
  const showChapters = !visibilitySettings.hideChapters;

  const handleTransitionSwitchChange = (
    section: SectionWithSlides,
    checked: boolean,
    chapterTemplateId: string | undefined
  ) => {
    // If the user is currently editing this chapter's transition in the
    // canvas, switch the view back to CHAPTER so the canvas stops rendering
    // the (now-disabled) transition editor. Build the chapter object from
    // the chapter template id — spreading currentChapter would keep the
    // transition template id and leak the transition's background into the
    // chapter view.
    if (
      !checked &&
      currentChapter.type === TemplateType.TRANSITION &&
      currentChapter.sectionId === section.id
    ) {
      // Switch away via handleCardClick (not the raw store setter) so
      // pending edits on this transition are saved before we navigate off it.
      handleCardClick?.({
        templateId: chapterTemplateId,
        topic: section.topic,
        sectionId: section.id,
        type: TemplateType.CHAPTER,
      });
    }
    setChapterTransitionVisibility(section.id, checked).catch((error) => {
      console.error("Error toggling chapter transition", error);
    });
  };

  return (
    <Flex
      style={{
        borderRadius: `${radius.box}px ${radius.box}px 0 0`,
        height: "calc(100% - 24px)",
        marginBlockStart: 24,
        marginInlineStart: 24,
        overflowY: "hidden",
        paddingInline: 0,
        paddingBlockStart: STORYBOARD_THUMBNAILS_PADDING,
        backgroundColor: theme.colorBgContainer,
        zIndex: 50,
      }}
    >
      <StyledScrollableFlex vertical gap={16}>
        {showIntro && (
          <LayoutWrapper>
            <Flex
              justify="space-between"
              align="center"
              style={{ width: THUMBNAIL_WRAPPER_WIDTH }}
            >
              <Typography.Text
                style={{
                  fontSize: "12px",
                  textTransform: "uppercase",
                }}
              >
                {t(
                  "components.templates.TemplatesBoard.storyboardSlidesThumbnails.introScene"
                )}
              </Typography.Text>
              {!showCheckbox && <IntroClosingEnableSwitch isIntro />}
            </Flex>
            {introEnabled && (
              <TemplatePreview
                {...generateCommonTemplateBoardPreviewProps({
                  ...introductionChapter,
                  templateId: introductionTemplate ?? undefined,
                })}
                onCardClick={() => {
                  handleCardClick?.({
                    ...introductionChapter,
                    templateId: introductionTemplate ?? undefined,
                  });
                }}
              />
            )}
          </LayoutWrapper>
        )}
        {sections.map((section, index) => {
          const currentChapterSettings = mediaGenerationSettings?.chapters.find(
            (chapter) => chapter.chapter_id === section.id
          );
          const transitionTemplate =
            currentChapterSettings?.transition_template_id ?? undefined;
          const chapterTemplate =
            currentChapterSettings?.chapter_template_id ?? undefined;

          const transitionChapter = {
            templateId: transitionTemplate,
            topic: section.topic,
            sectionId: section.id,
            type: TemplateType.TRANSITION,
          };

          const isCurrentChapter = currentChapter.topic === section.topic;

          const chapterHasTransition = getChapterHasTransition(
            currentChapterSettings,
            hasTransition
          );
          // Hide the per-chapter switch inside the Apply Settings drawer:
          // that flow uses checkboxes for bulk selection, so a switch on the
          // same row would be confusing.
          const showPerChapterSwitch = !showCheckbox;
          const showTransitionPreview = chapterHasTransition;
          // In the Apply Settings drawer, hide the entire transition row for
          // chapters that have transitions disabled — they can't be applied to.
          const hideTransitionRowInDrawer =
            showCheckbox && !chapterHasTransition;

          return (
            <Fragment key={section.id}>
              {showTransitions && !hideTransitionRowInDrawer && (
                <LayoutWrapper>
                  <Flex
                    justify="space-between"
                    align="center"
                    style={{ width: THUMBNAIL_WRAPPER_WIDTH }}
                  >
                    <Typography.Text
                      style={{
                        fontSize: "12px",
                        textTransform: "uppercase",
                      }}
                    >
                      {t(
                        "components.templates.TemplatesBoard.storyboardSlidesThumbnails.transitionScene"
                      ) +
                        " " +
                        (index + 1)}
                    </Typography.Text>
                    <Flex align="center" gap={8}>
                      {showCheckbox && (
                        <ChapterCheckbox
                          disabled={isCurrentChapter}
                          checked={
                            isCurrentChapter ||
                            checkedChapters.includes(transitionTemplate ?? "")
                          }
                          onChange={(e) =>
                            handleChapterCheckboxChange?.(e, transitionTemplate)
                          }
                        />
                      )}
                      {showPerChapterSwitch && (
                        <PerChapterTransitionSwitch
                          chapterNumber={index + 1}
                          labelAs="scene"
                          checked={chapterHasTransition}
                          onChange={(checked) =>
                            handleTransitionSwitchChange(
                              section,
                              checked,
                              chapterTemplate
                            )
                          }
                        />
                      )}
                    </Flex>
                  </Flex>
                  {showTransitionPreview && (
                    <TemplatePreview
                      {...generateCommonTemplateBoardPreviewProps(
                        transitionChapter
                      )}
                      previewText={section.topic}
                      onCardClick={() => {
                        handleCardClick?.(transitionChapter);
                      }}
                    />
                  )}
                </LayoutWrapper>
              )}
              {showChapters &&
                renderChapterWrapper(
                  getFirstSentence(parseScript(section.script.content).text),
                  {
                    templateId: chapterTemplate,
                    topic: section.topic,
                    sectionId: section.id,
                    type: TemplateType.CHAPTER,
                  },
                  index
                )}
            </Fragment>
          );
        })}
        {showClosing && (
          <LayoutWrapper>
            <Flex
              justify="space-between"
              align="center"
              style={{ width: THUMBNAIL_WRAPPER_WIDTH }}
            >
              <Typography.Text
                style={{
                  fontSize: "12px",
                  textTransform: "uppercase",
                }}
              >
                {t(
                  "components.templates.TemplatesBoard.storyboardSlidesThumbnails.closingScene"
                )}
              </Typography.Text>
              {!showCheckbox && <IntroClosingEnableSwitch isIntro={false} />}
            </Flex>
            {closingEnabled && (
              <TemplatePreview
                {...generateCommonTemplateBoardPreviewProps({
                  ...closingChapter,
                  templateId: closingTemplate ?? undefined,
                })}
                onCardClick={() => {
                  handleCardClick?.({
                    ...closingChapter,
                    templateId: closingTemplate ?? undefined,
                  });
                }}
              />
            )}
          </LayoutWrapper>
        )}
      </StyledScrollableFlex>
    </Flex>
  );
};
